import type { FC, ReactNode } from 'react'

import { Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Logo, LogoMark } from '@/components/brand/logo'
import CopyButton from '@/components/brand/copy-button'

interface LogoFile {
    title: string
    description: string
    file: string
    surface: string
    preview: ReactNode
}

const LOGO_FILES: LogoFile[] = [
    {
        title: 'Logo for dark backgrounds',
        description: 'The main logo. Use it on black, dark colours and darker photos.',
        file: '/brand/blitz-logo-on-dark.svg',
        surface: 'bg-ink',
        preview: <Logo variant='full' tone='dark' className='h-16 w-auto max-w-full sm:h-20' />
    },
    {
        title: 'Logo for light backgrounds',
        description: 'For white or light backgrounds, documents, quotes and invoices.',
        file: '/brand/blitz-logo-on-light.svg',
        surface: 'bg-paper',
        preview: <Logo variant='full' tone='light' className='h-16 w-auto max-w-full sm:h-20' />
    },
    {
        title: 'Yellow icon',
        description: 'For social media profiles, app icons and anywhere space is tight.',
        file: '/brand/blitz-icon-yellow.svg',
        surface: 'bg-ink-soft',
        preview: <LogoMark tone='volt' className='size-24' />
    },
    {
        title: 'Black icon',
        description: 'The same icon, for light backgrounds.',
        file: '/brand/blitz-icon-black.svg',
        surface: 'bg-paper',
        preview: <LogoMark tone='ink' className='size-24' />
    }
]

const COLOURS = [
    { name: 'Volt', hex: '#FFD60A', usage: 'The spark: buttons, highlights and the bolt.', swatch: 'bg-volt text-ink' },
    { name: 'Ink', hex: '#0B0B0E', usage: 'Backgrounds, headings and the icon tile.', swatch: 'bg-ink text-paper' },
    { name: 'Paper', hex: '#F6F4EF', usage: 'Light backgrounds and large areas of space.', swatch: 'bg-paper text-ink' },
    { name: 'Steel', hex: '#8B8E96', usage: 'Secondary text and fine details.', swatch: 'bg-steel text-ink' }
]

const RULES = [
    'Leave clear space around the logo, at least the height of the bolt in the icon.',
    'Use the yellow icon on dark backgrounds and the black icon on light ones.',
    'Keep the logo as it is: no stretching, new colours, outlines or shadows.'
]

const BrandPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Brand assets'
                description='Download the Blitz Tech Services logo and icon, and find the brand colours and fonts.'
            />

            <section aria-labelledby='brand-title' className='bg-ink text-paper'>
                <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8'>
                    <p className='text-sm font-semibold uppercase tracking-[0.18em] text-volt'>
                        Brand assets
                    </p>
                    <h1
                        id='brand-title'
                        className='mt-4 max-w-3xl text-balance font-heading text-4xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-6xl'
                    >
                        The Blitz Tech Services brand.
                    </h1>
                    <p className='mt-6 max-w-2xl text-lg leading-relaxed text-paper/70'>
                        Logo files, colours and fonts for everything you make, from social posts and quotes to vans and shop signs.
                    </p>

                    <div className='relative mt-12 flex justify-center overflow-hidden rounded-[2rem] border border-paper/10 bg-ink-soft px-6 py-16 sm:py-24'>
                        <div aria-hidden='true' className='absolute left-1/2 top-1/2 size-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/10 blur-3xl' />
                        <Logo variant='full' tone='dark' className='relative h-20 w-auto max-w-full sm:h-32' />
                    </div>
                </div>
            </section>

            <section id='logos' aria-labelledby='logos-title' className='bg-paper py-16 text-ink sm:py-24'>
                <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                    <h2 id='logos-title' className='font-heading text-3xl font-bold tracking-[-0.02em] sm:text-4xl'>
                        Logo files
                    </h2>
                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                        Vector files that stay sharp at any size, from a business card to a billboard. Any designer or printer can open them.
                    </p>

                    <div className='mt-10 grid gap-6 sm:grid-cols-2'>
                        {LOGO_FILES.map(item => (
                            <article key={item.file} className='overflow-hidden rounded-[1.75rem] border border-border bg-card'>
                                <div className={cn('flex h-56 items-center justify-center border-b border-border px-8', item.surface)}>
                                    {item.preview}
                                </div>
                                <div className='flex flex-col gap-5 p-6 sm:flex-row sm:items-end sm:justify-between'>
                                    <div>
                                        <h3 className='font-heading text-lg font-bold'>
                                            {item.title}
                                        </h3>
                                        <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                            {item.description}
                                        </p>
                                    </div>
                                    <a
                                        href={item.file}
                                        download
                                        className='inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-ink px-5 text-sm font-semibold text-paper transition-colors hover:bg-ink/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink'
                                    >
                                        <Download className='size-4' aria-hidden='true' />
                                        Download SVG
                                    </a>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id='colours' aria-labelledby='colours-title' className='border-t border-border bg-card py-16 text-ink sm:py-24'>
                <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                    <h2 id='colours-title' className='font-heading text-3xl font-bold tracking-[-0.02em] sm:text-4xl'>
                        Colours
                    </h2>
                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                        Yellow does the shouting, ink and paper do the rest. Tap a colour code to copy it.
                    </p>

                    <div className='mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                        {COLOURS.map(colour => (
                            <div key={colour.name} className='overflow-hidden rounded-[1.75rem] border border-border'>
                                <div className={cn('flex h-40 items-end justify-between p-5', colour.swatch)}>
                                    <span className='font-heading text-xl font-bold'>
                                        {colour.name}
                                    </span>
                                    <CopyButton value={colour.hex} label={colour.name} className='bg-black/10 hover:bg-black/20' />
                                </div>
                                <p className='p-5 text-sm leading-relaxed text-muted-foreground'>
                                    {colour.usage}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id='typography' aria-labelledby='typography-title' className='border-t border-border bg-paper py-16 text-ink sm:py-24'>
                <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                    <h2 id='typography-title' className='font-heading text-3xl font-bold tracking-[-0.02em] sm:text-4xl'>
                        Typography
                    </h2>
                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                        Two free Google Fonts: a bold, wide face for headlines and a clean one for everything else.
                    </p>

                    <div className='mt-10 grid gap-6 lg:grid-cols-2'>
                        <div className='rounded-[1.75rem] border border-border bg-card p-8'>
                            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-ink/50'>
                                Headlines · Unbounded
                            </p>
                            <p className='mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-5xl'>
                                Brilliant websites.
                            </p>
                            <p className='mt-6 font-heading text-lg text-ink/60'>
                                Aa Bb Cc Dd Ee Ff Gg 0123456789
                            </p>
                        </div>

                        <div className='rounded-[1.75rem] border border-border bg-card p-8'>
                            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-ink/50'>
                                Body text · Manrope
                            </p>
                            <p className='mt-6 text-xl leading-relaxed'>
                                We design and build websites for local businesses and big companies, at prices that make sense.
                            </p>
                            <p className='mt-6 text-lg text-ink/60'>
                                Aa Bb Cc Dd Ee Ff Gg 0123456789
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section id='usage' aria-labelledby='usage-title' className='bg-ink py-16 text-paper sm:py-24'>
                <div className='mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.4fr] lg:px-8'>
                    <div>
                        <h2 id='usage-title' className='font-heading text-3xl font-bold tracking-[-0.02em] sm:text-4xl'>
                            Using the logo
                        </h2>
                        <p className='mt-4 max-w-md text-lg leading-relaxed text-paper/70'>
                            A few simple rules keep the brand looking sharp wherever it appears.
                        </p>
                    </div>

                    <ol className='space-y-4'>
                        {RULES.map((rule, index) => (
                            <li key={rule} className='flex items-start gap-4 rounded-2xl border border-paper/10 bg-ink-soft p-5'>
                                <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-volt font-heading text-sm font-bold text-ink'>
                                    {index + 1}
                                </span>
                                <p className='pt-1 leading-relaxed text-paper/85'>
                                    {rule}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>
        </Layout>
    )
}

export default BrandPage
